import { Unit } from '../types/units';
import { BlueprintLocation, LevelModel, RoomModel } from '../types/protocols';
import { CustomerType } from '../types/customerType';
import { RoomType, UnitKind, UnitTenure } from '../types/Enums';
import { naturalCompare } from './naturalSort';
import { roomLevelKeys } from './roomUtils';
import { RENTED_COLOR, VACANT_COLOR } from './leaseStatusColors';

/**
 * Pure helpers for units (lägenheter and lokaler). A unit is a group of rooms
 * (uniform rooms rule, plan D5): the levels it is on and its pin on each
 * drawing are derived from the rooms here and never stored.
 */

/** Room types that mean "this room is the whole unit" (plan D13). */
export const WHOLE_UNIT_ROOM_TYPES: readonly RoomType[] = [RoomType.RoomApartment, RoomType.RoomCommercial];

export const isWholeUnitRoomType = (type: RoomType | undefined): boolean =>
    type !== undefined && WHOLE_UNIT_ROOM_TYPES.includes(type);

/** The whole-unit room type for a kind (plan D13). */
export const wholeUnitRoomType = (kind: UnitKind): RoomType =>
    kind === UnitKind.Commercial ? RoomType.RoomCommercial : RoomType.RoomApartment;

/** Type icon per kind (colour PNG in public/, drawn as it is): a door for a lägenhet, a storefront for a lokal. */
const UNIT_TYPE_ICONS: Record<UnitKind, string> = {
    [UnitKind.Apartment]: '/Images/UI/unit-apartment.png',
    [UnitKind.Commercial]: '/Images/UI/unit-commercial.png',
};

/** Icon URL for a unit kind; an unknown kind falls back to the lägenhet. */
export const unitTypeIcon = (kind: UnitKind): string =>
    UNIT_TYPE_ICONS[kind] ?? UNIT_TYPE_ICONS[UnitKind.Apartment];

/** "Lägenhet 1203" / "Lokal 3" — also the lease label snapshot (plan D7: no level, numbers are building-unique). */
export const unitDisplayName = (unit: Pick<Unit, 'kind' | 'number'>): string => {
    const noun = unit.kind === UnitKind.Commercial ? 'Lokal' : 'Lägenhet';
    const trimmed = (unit.number || '').trim();
    return trimmed.length > 0 ? `${noun} ${trimmed}` : `${noun} utan nummer`;
};

/** Apartments first, then commercial; natural number order within a kind (2 < 10, A3 < A12), key as tie-breaker. */
export const sortedUnits = (units: { [unitKey: string]: Unit } | Unit[] | undefined): Unit[] => {
    const list = Array.isArray(units) ? units : Object.values(units || {});
    const kindOrder = (kind: UnitKind) => (kind === UnitKind.Apartment ? 0 : 1);
    return [...list].sort((a, b) => {
        const byKind = kindOrder(a.kind) - kindOrder(b.kind);
        if (byKind !== 0) return byKind;
        const byNumber = naturalCompare(a.number || '', b.number || '');
        return byNumber !== 0 ? byNumber : a.key.localeCompare(b.key);
    });
};

export const unitRoomKeys = (unit: Pick<Unit, 'rooms'>): string[] => Object.keys(unit.rooms || {});

/**
 * The distinct levels of the unit's existing rooms (plan D5), every level of
 * a room that spans several. Dangling room keys and rooms without a level
 * contribute nothing.
 */
export const unitLevelKeys = (
    unit: Pick<Unit, 'rooms'>,
    rooms: { [key: string]: Pick<RoomModel, 'levelKey' | 'levelKeys'> } | undefined
): string[] => {
    const seen = new Set<string>();
    for (const roomKey of unitRoomKeys(unit)) {
        const room = rooms?.[roomKey];
        if (room) roomLevelKeys(room).forEach(levelKey => seen.add(levelKey));
    }
    return Array.from(seen);
};

/** Units none of whose linked rooms still exists: unplaced, listed under "Utan rum" rather than on a level. */
export const unitsWithoutRooms = (
    units: Unit[],
    rooms: { [key: string]: unknown } | undefined
): Unit[] =>
    units.filter(unit => !unitRoomKeys(unit).some(key => !!rooms?.[key]));

/** Units with a room on `levelKey`; a multi-level unit appears on every one of its levels. */
export const unitsOnLevel = (
    units: Unit[],
    levelKey: string,
    rooms: { [key: string]: Pick<RoomModel, 'levelKey' | 'levelKeys'> } | undefined
): Unit[] =>
    units.filter(unit => unitLevelKeys(unit, rooms).includes(levelKey));

/** The unit holding `roomKey`, if any (a room belongs to at most one unit, plan D6). */
export const unitHoldingRoom = (
    units: Pick<Unit, 'key' | 'rooms'>[] | { [unitKey: string]: Unit },
    roomKey: string,
    excludingKey?: string
): Pick<Unit, 'key' | 'rooms'> | undefined => {
    const list = Array.isArray(units) ? units : Object.values(units);
    return list.find(unit => unit.key !== excludingKey && unit.rooms?.[roomKey] === true);
};

/** Rented state is the explicit `currentLeaseKey` written by leasesApi; no date logic here. */
export const isUnitRented = (unit: Pick<Unit, 'currentLeaseKey'>): boolean =>
    typeof unit.currentLeaseKey === 'string' && unit.currentLeaseKey.length > 0;

/** Only hyresrätt units carry leases (plan D3). */
export const isUnitLeasable = (unit: Pick<Unit, 'tenure'>): boolean => unit.tenure === UnitTenure.Hyresratt;

/** A drawing marker's lease state: rented or free for a leasable unit, none (no icon) for bostadsrätt and own use. */
export const unitMarkerRented = (unit: Pick<Unit, 'tenure' | 'currentLeaseKey'>): boolean | undefined =>
    isUnitLeasable(unit) ? isUnitRented(unit) : undefined;

/** Marker colour: green only for a hyresrätt without a lease; rented (an upcoming-only lease too), bostadsrätt and own use are grey. */
export const unitStatusColor = (unit: Pick<Unit, 'tenure' | 'currentLeaseKey'>): string =>
    isUnitLeasable(unit) && !isUnitRented(unit) ? VACANT_COLOR : RENTED_COLOR;

/** Leasable units without a lease; bostadsrätt and own-use units are never "vacant". */
export const vacantUnitCount = (units: Pick<Unit, 'currentLeaseKey' | 'tenure'>[]): number =>
    units.filter(unit => isUnitLeasable(unit) && !isUnitRented(unit)).length;

/** Sum of `area` over the units, ignoring units without one. */
export const totalUnitArea = (units: Pick<Unit, 'area'>[]): number =>
    units.reduce((sum, unit) => sum + (typeof unit.area === 'number' && Number.isFinite(unit.area) ? unit.area : 0), 0);

/** Levels a unit can occupy: every level, highest number first. */
export const unitLevelsDescending = (levels: { [key: string]: LevelModel } | undefined): LevelModel[] =>
    Object.values(levels || {})
        .sort((a, b) => b.number - a.number || a.key.localeCompare(b.key));

const hasCurrentBlueprint = (level: LevelModel): boolean =>
    !!level.currentBlueprintKey && !!level.blueprints?.[level.currentBlueprintKey];

const lowestNonNegative = (levels: LevelModel[]): LevelModel | undefined =>
    [...levels].sort((a, b) => a.number - b.number || a.key.localeCompare(b.key)).find(level => level.number >= 0)
    ?? [...levels].sort((a, b) => a.number - b.number || a.key.localeCompare(b.key))[0];

/**
 * Default level for the units screen: the lowest level >= 0 that has a
 * drawing and at least one unit; else the lowest level >= 0 with a drawing;
 * else the lowest level >= 0. Apartments and lokaler sit above ground, which
 * is why this follows the rooms tab rather than the storage units' basement rule.
 */
export const defaultUnitLevelKey = (
    levels: { [key: string]: LevelModel } | LevelModel[] | undefined,
    units: Pick<Unit, 'rooms'>[],
    rooms: { [key: string]: Pick<RoomModel, 'levelKey' | 'levelKeys'> } | undefined
): string | null => {
    const candidates = Array.isArray(levels) ? levels : Object.values(levels || {});
    if (candidates.length === 0) return null;
    const withDrawing = candidates.filter(hasCurrentBlueprint);
    const levelKeysWithUnits = new Set(units.flatMap(unit => unitLevelKeys(unit, rooms)));
    return (
        lowestNonNegative(withDrawing.filter(level => levelKeysWithUnits.has(level.key))) ??
        lowestNonNegative(withDrawing) ??
        lowestNonNegative(candidates)
    )?.key ?? null;
};

/**
 * Another unit of the same kind already using `number` anywhere in the
 * building, or null. Unit numbers are building-unique in practice (plan D7
 * labels carry no level), so this is a soft warning only.
 */
export const duplicateUnitNumber = (
    units: Pick<Unit, 'key' | 'kind' | 'number'>[],
    kind: UnitKind,
    number: string,
    excludingKey?: string
): boolean => {
    const wanted = (number || '').trim().toUpperCase();
    if (!wanted) return false;
    return units.some(unit =>
        unit.key !== excludingKey &&
        unit.kind === kind &&
        (unit.number || '').trim().toUpperCase() === wanted
    );
};

/**
 * Tenures offered by the form (plan D4): a bostadsrätt only exists inside a
 * bostadsrättsförening, so it is hidden for fastighetsägare and stiftelse
 * instances. A missing or unknown customer type shows everything.
 */
export const selectableTenures = (customerType: CustomerType | null): UnitTenure[] =>
    customerType === 'fastighetsagare' || customerType === 'stiftelse'
        ? [UnitTenure.Hyresratt, UnitTenure.Own]
        : [UnitTenure.Bostadsratt, UnitTenure.Hyresratt, UnitTenure.Own];

/** Default tenure for a new unit (plan D4): bostadsrätt in a BRF instance for apartments, hyresrätt otherwise. */
export const defaultTenure = (customerType: CustomerType | null, kind: UnitKind): UnitTenure =>
    customerType === 'bostadsrattsforening' && kind === UnitKind.Apartment ? UnitTenure.Bostadsratt : UnitTenure.Hyresratt;

/** The unit's rooms that carry a marker on drawing `imageKey`. */
export const placedUnitRooms = (
    unit: Pick<Unit, 'rooms'>,
    rooms: { [key: string]: RoomModel } | undefined,
    imageKey: string
): RoomModel[] =>
    unitRoomKeys(unit)
        .map(key => rooms?.[key])
        .filter((room): room is RoomModel => !!room && !!room.blueprintLocations?.[imageKey]);

/** True when the unit shows on drawing `imageKey`: at least one of its rooms is placed there. */
export const isUnitPlacedOn = (
    unit: Pick<Unit, 'rooms'>,
    rooms: { [key: string]: RoomModel } | undefined,
    imageKey: string
): boolean => placedUnitRooms(unit, rooms, imageKey).length > 0;

/**
 * The room to move when the unit's pin is dragged on drawing `imageKey`:
 * only a unit with exactly one room placed there is movable as a pin (plan
 * D5); with several rooms the rooms are moved on the level page.
 */
export const movableUnitRoom = (
    unit: Pick<Unit, 'rooms'>,
    rooms: { [key: string]: RoomModel } | undefined,
    imageKey: string
): RoomModel | undefined => {
    const placed = placedUnitRooms(unit, rooms, imageKey);
    return placed.length === 1 ? placed[0] : undefined;
};

/**
 * The unit's pin on drawing `imageKey`: the centroid of its placed rooms
 * (drawing size taken from the first room marker). Computed, never stored;
 * iOS derives the same from the building.
 */
export const derivedUnitLocation = (
    unit: Pick<Unit, 'rooms'>,
    rooms: { [key: string]: RoomModel } | undefined,
    imageKey: string
): BlueprintLocation | undefined => {
    const placed = placedUnitRooms(unit, rooms, imageKey).map(room => room.blueprintLocations![imageKey]);
    if (placed.length === 0) return undefined;
    const x = Math.round(placed.reduce((sum, loc) => sum + loc.x, 0) / placed.length);
    const y = Math.round(placed.reduce((sum, loc) => sum + loc.y, 0) / placed.length);
    return { key: imageKey, x, y, width: placed[0].width, height: placed[0].height };
};

/** Pins for every drawing the unit's rooms are placed on. Feeds `NumberedMarker.blueprintLocations`. */
export const effectiveUnitLocations = (
    unit: Pick<Unit, 'rooms'>,
    rooms: { [key: string]: RoomModel } | undefined
): { [imageKey: string]: BlueprintLocation } => {
    const result: { [imageKey: string]: BlueprintLocation } = {};
    const imageKeys = new Set<string>();
    for (const key of unitRoomKeys(unit)) {
        for (const imageKey of Object.keys(rooms?.[key]?.blueprintLocations || {})) imageKeys.add(imageKey);
    }
    for (const imageKey of imageKeys) {
        const derived = derivedUnitLocation(unit, rooms, imageKey);
        if (derived) result[imageKey] = derived;
    }
    return result;
};

/** Responsibility note shown on bostadsrätt units. */
export const BOSTADSRATT_RESPONSIBILITY_NOTE = 'Bostadsrättshavaren ansvarar för inre underhåll';
